import React, { useState, useEffect } from 'react';
import { BUTTON_TEXT, ANIMATIONS } from '../constants';
import { useGameAudio } from './GameAudioProvider';

interface HowToPlayModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameTitle: string;
  instructions: string[];
}

const HowToPlayModal: React.FC<HowToPlayModalProps> = ({
  isOpen,
  onClose,
  gameTitle,
  instructions
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const { kinzySpeak } = useGameAudio();

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Voice narration when modal opens
      const timer = setTimeout(() => {
        kinzySpeak(`How to play ${gameTitle}. Here are the instructions to master the game.`);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden transform transition-all ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-2">
                How to Play
              </h3>
              <p className="text-white/90 text-lg">
                {gameTitle}
              </p>
            </div>

            <button
              onClick={handleClose}
              className="bg-white/20 hover:bg-white/30 rounded-full p-2 transition-all duration-200"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {instructions.map((instruction, index) => (
              <div
                key={index}
                className={`flex items-start space-x-4 p-4 bg-gray-50 rounded-xl transform transition-all ${
                  isAnimating ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                }`}
              >
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </div>

                <div className="flex-1">
                  <p className="text-gray-700 leading-relaxed">
                    {instruction}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-xl">
            <h4 className="text-lg font-bold text-blue-900 mb-3 flex items-center">
              <span className="mr-2">🎮</span>
              Game Controls
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-white rounded border shadow-sm text-xs">Click</kbd>
                <span className="text-blue-800">Place your symbol</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-white rounded border shadow-sm text-xs">Touch</kbd>
                <span className="text-blue-800">Mobile friendly</span>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 rounded-xl">
            <h4 className="text-lg font-bold text-yellow-900 mb-3 flex items-center">
              <span className="mr-2">💡</span>
              Tips for Success
            </h4>
            <ul className="space-y-2 text-sm text-yellow-800">
              <li className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-1">•</span>
                <span>Try to get three symbols in a row, column, or diagonal</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-1">•</span>
                <span>Block your opponent when they have two in a row</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-1">•</span>
                <span>Control the center square for better winning chances</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-1">•</span>
                <span>Win multiple games to earn more stars and unlock new kingdoms</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="p-6 bg-gray-50 border-t">
          <div className="flex justify-center">
            <button
              onClick={handleClose}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 active:scale-95"
            >
              {BUTTON_TEXT.CONTINUE} 🚀
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { HowToPlayModal };